import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { useRecordings } from '../../contexts/RecordingContext';
import { recordings as recordingsApi } from '../../services/api';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ArrowLeft,
  Play,
  Pause,
  Download,
  Share2,
  Edit,
  Trash2,
  Globe,
  Lock,
  Eye,
  Clock,
  Calendar,
  Music,
  Loader2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import AudioSpectrumVisualizer from './AudioSpectrumVisualizer';
import WaveformPlayer from './WaveformPlayer';
import ShareDialog from './ShareDialog';
import EditRecordingDialog from './EditRecordingDialog';

const RecordingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { play, pause, isPlaying, currentRecording, audioRef, currentTime, duration, seek } = useAudioPlayer();
  const { deleteRecording } = useRecordings();

  const [recording, setRecording] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    fetchRecordingDetails();
  }, [id]);

  const fetchRecordingDetails = async () => {
    try {
      setIsLoading(true);
      const response = await recordingsApi.get(id!);
      setRecording(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching recording:', err);
      setError('Failed to load recording');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayPause = async () => {
    if (!recording) return;

    if (currentRecording?.id === recording.id && isPlaying) {
      pause();
    } else {
      await play(recording);
    }
  };

  const handleDelete = async () => {
    if (!recording) return;

    if (window.confirm(`Are you sure you want to delete "${recording.title}"?`)) {
      try {
        await deleteRecording(recording.id);
        navigate('/recordings');
      } catch (err) {
        console.error('Failed to delete recording:', err);
      }
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error || !recording) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>{error || 'Recording not found'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/recordings')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Recordings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCurrentlyPlaying = currentRecording?.id === recording.id && isPlaying;

  return (
    <div className="container mx-auto p-6 pb-32 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/recordings')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {recording.title}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {recording.venueName || recording.venue_name || 'Unknown Venue'}
          </p>
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Artwork and visualizer */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardContent className="p-6">
              {/* Waveform player with progress */}
              <div className="mb-6">
                <WaveformPlayer
                  waveformData={recording.waveformData || recording.waveform_data || []}
                  currentTime={currentRecording?.id === recording.id ? currentTime : 0}
                  duration={currentRecording?.id === recording.id ? duration : recording.durationSeconds || recording.duration_seconds || 0}
                  onSeek={seek}
                  isPlaying={isCurrentlyPlaying}
                  height={300}
                />
              </div>

              {/* Live spectrum visualizer (optional - shown when playing) */}
              {currentRecording?.id === recording.id && audioRef.current && isCurrentlyPlaying && (
                <motion.div
                  className="mb-6 bg-gradient-to-br from-purple-900 via-pink-900 to-purple-900 rounded-lg overflow-hidden"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 200 }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <AudioSpectrumVisualizer
                    audioElement={audioRef.current}
                    width={800}
                    height={200}
                    barCount={64}
                    barColor="#A855F7"
                    style="bars"
                  />
                </motion.div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={handlePlayPause}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {isCurrentlyPlaying ? (
                    <>
                      <Pause className="mr-2 h-4 w-4" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Play
                    </>
                  )}
                </Button>

                <Button variant="outline" onClick={() => setShareDialogOpen(true)}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </Button>

                {(recording.downloadUrl || recording.download_url) && (
                  <Button variant="outline" asChild>
                    <a href={recording.downloadUrl || recording.download_url} download>
                      <Download className="mr-2 h-4 w-4" />
                      Download
                    </a>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          {recording.description && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {recording.description}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column - Details */}
        <div className="space-y-6">
          {/* Info card */}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
                <Badge
                  variant={recording.status === 'ready' ? 'default' :
                          recording.status === 'failed' ? 'destructive' : 'outline'}
                >
                  {recording.status}
                </Badge>
              </div>

              {/* Visibility */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Visibility</span>
                <Badge variant="outline" className={recording.isPublic ? 'text-green-600' : 'text-gray-600'}>
                  {recording.isPublic ? (
                    <>
                      <Globe className="mr-1 h-3 w-3" />
                      Public
                    </>
                  ) : (
                    <>
                      <Lock className="mr-1 h-3 w-3" />
                      Private
                    </>
                  )}
                </Badge>
              </div>

              {/* Duration */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Duration
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatDuration(recording.durationSeconds || recording.duration_seconds || 0)}
                </span>
              </div>

              {/* Created */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Created
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {new Date(recording.startTime || recording.start_time || recording.created_at).toLocaleDateString()}
                </span>
              </div>

              {/* Views */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  Views
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {recording.viewCount || recording.view_count || 0}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          {recording.tags && recording.tags.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {recording.tags.map((tag: string, index: number) => (
                    <Badge key={index} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => setEditDialogOpen(true)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit Recording
              </Button>

              <Button
                variant="outline"
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={handleDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Recording
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      {recording && (
        <>
          <ShareDialog
            recording={recording}
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
          />
          <EditRecordingDialog
            recording={recording}
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              fetchRecordingDetails(); // Refresh after edit
            }}
          />
        </>
      )}
    </div>
  );
};

export default RecordingDetail;
